import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Dialog, DialogContent, IconButton } from "@mui/material";
import { PDFViewer } from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { DELETE_CUSTOM_INVOICE, EDIT_CUSTOM_INVOICE, VIEW_CUSTOM_INVOICE } from "../../constants";
import { getInvoiceTableColumns } from "../../pages/invoices/constants";
import { CompanyService, CRMService, InvoiceService, ProjectService } from "../../services";
import { useHasPermission } from "../../utils";
import { get_date } from "../generalComponets";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { CustomInvoicePdf, getInvoicePdfPayload } from "./CustomInvoicePdf.template";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const CustomInvoiceListComponent = () => {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, data: null });
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const { paginationModel: paginationState, setPaginationModel: setPaginationState } = usePersistedTableState({
    tableId: "invoices",
    defaults: {},
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    invoiceId: null,
  });

  const hasViewInvoice = useHasPermission(VIEW_CUSTOM_INVOICE);
  const hasEditInvoice = useHasPermission(EDIT_CUSTOM_INVOICE);
  const hasDeleteInvoice = useHasPermission(DELETE_CUSTOM_INVOICE);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  useEffect(() => {
    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      type: "CUSTOM",
      sortBy: "createdAt",
      sortOrder: "desc",
    });
  }, [paginationState]);

  const fetchData = async (filters) => {
    setLoading(true);
    try {
      const { data: invoices } = await InvoiceService.getAllInvoices(filters);
      setInvoices(invoices?.rows);
      setTotalCount(invoices?.count);
    } catch (error) {
      console.error(`🚀 ~ CustomInvoiceList.component.jsx ~ fetchData ~ error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleViewClick = async (row) => {
    if (!hasViewInvoice) return;
    setLoading(true);
    try {
      const { data: invoice } = await InvoiceService.getInvoiceById(row.id);
      const { data: client } = await CRMService.getClientById(invoice?.clientId);
      const { data: company } = await CompanyService.getCompanyDetails(user?.company?.id);

      const pdfData = getInvoicePdfPayload(invoice, client, company);
      setPdfModal({ open: true, data: pdfData });
    } catch (error) {
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (invoice) => {
    setDeleteModal({
      isOpen: true,
      invoiceId: invoice?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      invoiceId: null,
    });
  };

  const closePdfModal = () => setPdfModal({ open: false, data: null });

  const handleDelete = async () => {
    setLoading(true);
    try {
      await InvoiceService.deleteInvoice(deleteModal.invoiceId);
      toast.success("Invoice deleted successfully");
      fetchData({
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        type: "CUSTOM",
        sortBy: "createdAt",
        sortOrder: "desc",
      });
    } catch (error) {
      toast.error("Failed to delete invoice");
      console.log(`🚀 ~ deleteInvoice ~ error:`, error);
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  const handleActions = async (invoiceData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/invoices/${invoiceData.id}/edit`);
        break;
      case "delete":
        openDeleteModal(invoiceData);
        break;
      default:
        return;
    }
  };

  const columnHeaders = getInvoiceTableColumns(handleActions, hasEditInvoice, hasDeleteInvoice);

  const rows = invoices.map((invoice, index) => {
    return {
      sl: index + 1,
      id: invoice.id,
      invoiceNumber: invoice?.invoiceNumber,
      date: get_date(invoice?.date),
      amount: invoice?.totalAmount,
    };
  });
  return (
    <>
      <CardLayout>
        <MTable
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount}
          loading={loading}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={handleViewClick}
        />
      </CardLayout>
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this invoice?"
      />

      {/* PDF Modal */}
      <Dialog
        open={pdfModal.open}
        onClose={closePdfModal}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { height: "100vh" } } }}
      >
        <IconButton onClick={closePdfModal} sx={{ position: "absolute", top: 6, right: -6, zIndex: 1 }}>
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0, height: "100%" }}>
          {pdfModal.data ? (
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <CustomInvoicePdf data={pdfModal.data} />
            </PDFViewer>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
